// noise-canvas.js

export function createNoiseCanvas(canvas, options = {}) {
  const {
    color1   = [1.0, 1.0, 1.0],
    color2   = [0.5, 0.5, 1.0],
    mode     = 'mono',
    density  = 0.96,
    opacity  = 0.6,
    cellSize = 0.75,
    blend    = 'screen',
    alphaFloor = 0.0,
  } = options;

  canvas.style.mixBlendMode = blend;

  const gl = canvas.getContext('webgl');
  if (!gl) {
    console.warn('noise-canvas: WebGL not supported');
    return null;
  }

  const vertSrc = `
    attribute vec2 a_position;
    void main() {
      gl_Position = vec4(a_position, 0.0, 1.0);
    }
  `;

  const fragSrc = `
    precision mediump float;

    uniform vec2  u_resolution;
    uniform vec3  u_color1;
    uniform vec3  u_color2;
    uniform int   u_mode;
    uniform float u_density;
    uniform float u_opacity;
    uniform float u_cellSize;
    uniform float u_alphaFloor;

    float hash(vec2 p) {
      p = fract(p * vec2(234.34, 435.345));
      p += dot(p, p + 34.23);
      return fract(p.x * p.y);
    }

    void main() {
      vec2 cell = floor(gl_FragCoord.xy / u_cellSize);
      float speck = smoothstep(u_density, 1.0, hash(cell));

      vec3 color;

      if (u_mode == 0) {
        color = u_color1;
      } else {
        float colorSelect = hash(cell + 100.0);
        color = colorSelect > 0.5 ? u_color2 : u_color1;
      }

      float alpha = speck > 0.0 ? u_alphaFloor + (speck * (u_opacity - u_alphaFloor)) : 0.0;
      gl_FragColor = vec4(color, alpha);
    }
  `;

  function compileShader(type, src) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('noise-canvas shader error:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  const vert = compileShader(gl.VERTEX_SHADER, vertSrc);
  const frag = compileShader(gl.FRAGMENT_SHADER, fragSrc);
  if (!vert || !frag) return null;

  const program = gl.createProgram();
  gl.attachShader(program, vert);
  gl.attachShader(program, frag);
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('noise-canvas program error:', gl.getProgramInfoLog(program));
    return null;
  }

  gl.useProgram(program);

  const vertices = new Float32Array([-1,-1, 1,-1, -1,1, 1,1]);
  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

  const positionLoc = gl.getAttribLocation(program, 'a_position');
  gl.enableVertexAttribArray(positionLoc);
  gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0);

  const resolutionLoc = gl.getUniformLocation(program, 'u_resolution');
  const color1Loc     = gl.getUniformLocation(program, 'u_color1');
  const color2Loc     = gl.getUniformLocation(program, 'u_color2');
  const modeLoc       = gl.getUniformLocation(program, 'u_mode');
  const densityLoc    = gl.getUniformLocation(program, 'u_density');
  const opacityLoc    = gl.getUniformLocation(program, 'u_opacity');
  const cellSizeLoc   = gl.getUniformLocation(program, 'u_cellSize');
  const alphaFloorLoc = gl.getUniformLocation(program, 'u_alphaFloor');

  gl.uniform3fv(color1Loc,  color1);
  gl.uniform3fv(color2Loc,  color2);
  gl.uniform1i(modeLoc,     mode === 'duo' ? 1 : 0);
  gl.uniform1f(densityLoc,  density);
  gl.uniform1f(opacityLoc,  opacity);
  gl.uniform1f(cellSizeLoc, cellSize);
  gl.uniform1f(alphaFloorLoc, alphaFloor);

  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  function draw() {
    canvas.width  = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform2f(resolutionLoc, canvas.width, canvas.height);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  // Debounced resize — redraw when container dimensions change
  let resizeTimer;
  const ro = new ResizeObserver(() => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(draw, 100);
  });
  ro.observe(canvas);
  draw();

  return {
    destroy() {
      clearTimeout(resizeTimer);
      ro.disconnect();
      gl.deleteProgram(program);
      gl.deleteShader(vert);
      gl.deleteShader(frag);
      gl.deleteBuffer(buffer);
    }
  };
}